/**
 * CloseLiabilityDialog — Phase 85.4
 *
 * Close a loan because it's paid off, or retire it into the loan that
 * refinanced it. Refinancing keeps the old loan's payment history visible on
 * the replacement — nothing is deleted or rewritten.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { closeLiability } from '@/lib/liabilityReconcileApi';

export function CloseLiabilityDialog({
  open, onOpenChange, liabilityId, liabilityName, otherLiabilities = [], onClosed,
}) {
  const [reason, setReason] = useState('paid_off');
  const [replacement, setReplacement] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason === 'refinanced' && !replacement) {
      toast.error('Pick the loan that replaced this one.');
      return;
    }
    setBusy(true);
    try {
      const res = await closeLiability(liabilityId, {
        reason,
        replacementLiabilityId: reason === 'refinanced' ? replacement : undefined,
      });
      toast.success(reason === 'paid_off' ? 'Loan marked as paid off.' : 'Loan retired into its replacement.');
      onOpenChange(false);
      onClosed?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || 'Could not close that loan.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="close-liability-dialog">
        <DialogHeader>
          <DialogTitle>Close {liabilityName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm">What happened to this loan?</p>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="close-liability-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="paid_off" data-testid="close-reason-paid-off">I paid it off</SelectItem>
                <SelectItem value="refinanced" data-testid="close-reason-refinanced">I refinanced it</SelectItem>
                <SelectItem value="closed" data-testid="close-reason-closed">Closed for another reason</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {reason === 'refinanced' && (
            <div className="space-y-2">
              <p className="text-sm">Which loan replaced it?</p>
              <Select value={replacement} onValueChange={setReplacement}>
                <SelectTrigger data-testid="close-liability-replacement">
                  <SelectValue placeholder="Choose the new loan" />
                </SelectTrigger>
                <SelectContent>
                  {otherLiabilities.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`close-replacement-${l.id}`}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                The new loan will show this loan's payment history alongside its own.
              </p>
            </div>
          )}

          <p className="text-xs text-muted-foreground leading-relaxed">
            The balance is set to zero as of today, so this debt stops counting toward
            your net worth and your debt-free date. Its payments stay on record.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" size="sm" disabled={busy}
                  onClick={() => onOpenChange(false)} data-testid="close-liability-cancel">
            Cancel
          </Button>
          <Button size="sm" disabled={busy} onClick={submit} data-testid="close-liability-confirm">
            {busy ? 'Closing…' : 'Close this loan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CloseLiabilityDialog;
